import { useState, useEffect } from 'react';
import { walletAPI, caddieAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Copy, Check, MessageCircle, User, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

function SendMoneyDialog({ open, onOpenChange, onSuccess, searchCaddies, prefilledCaddie }) {
  const [sendMoneyData, setSendMoneyData] = useState({ recipient_identifier: '', amount: '', notes: '' });
  const [referenceData, setReferenceData] = useState({ recipient_phone: '', recipient_name: '', amount: '', notes: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [generatedReference, setGeneratedReference] = useState(null);
  const [copied, setCopied] = useState({ code: false, pin: false, sms: false });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [registeredCaddies, setRegisteredCaddies] = useState([]);
  const [selectedCaddie, setSelectedCaddie] = useState(null);
  const [activeTab, setActiveTab] = useState('registered');
  const [sendRequiresOtp, setSendRequiresOtp] = useState(false);
  const [sendOtp, setSendOtp] = useState('');
  const [referenceRequiresOtp, setReferenceRequiresOtp] = useState(false);
  const [referenceOtp, setReferenceOtp] = useState('');
  const [paymentSettings, setPaymentSettings] = useState({
    denominations: [50, 100, 150, 200],
    recommended_amount: 100,
    allow_custom_amount: true,
  });

  const hasLinkedCaddiePayAccount = (caddie) => Boolean(caddie?.linked_caddie_user_id);

  const resolveRecipientIdentifier = (caddie) => {
    if (!caddie) return '';
    return (
      caddie.linked_caddie_user_id ||
      caddie.caddie_email ||
      caddie.caddie_phone ||
      ''
    );
  };

  const resolveCaddieDisplayName = (caddie) => (
    `${caddie?.caddie_name || ''} ${caddie?.caddie_surname || ''}`.trim()
  );

  const selectCaddieForPayment = (caddie) => {
    const displayName = resolveCaddieDisplayName(caddie);

    setSelectedCaddie(caddie);
    setSendRequiresOtp(false);
    setSendOtp('');
    setReferenceRequiresOtp(false);
    setReferenceOtp('');
    setShowConfirmation(false);

    setSendMoneyData({
      recipient_identifier: resolveRecipientIdentifier(caddie),
      amount: String(paymentSettings.recommended_amount),
      notes: caddie?.notes || ''
    });

    setReferenceData({
      recipient_phone: caddie?.caddie_phone || '',
      recipient_name: displayName,
      amount: String(paymentSettings.recommended_amount),
      notes: caddie?.notes || ''
    });

    setActiveTab(hasLinkedCaddiePayAccount(caddie) ? 'registered' : 'reference');
  };

  // Fetch registered caddies when dialog opens
  useEffect(() => {
    if (open) {
      caddieAPI.getRegisteredCaddies()
        .then(res => setRegisteredCaddies(res.data.caddies || []))
        .catch(() => setRegisteredCaddies([]));
      walletAPI.getPaymentSettings()
        .then((res) => setPaymentSettings(res.data))
        .catch(() => toast.error('Could not load the current payment amount options'));
    }
  }, [open]);

  // Pre-fill form when prefilledCaddie changes
  useEffect(() => {
    if (prefilledCaddie) {
      const displayName = `${prefilledCaddie.caddie_name || ''} ${prefilledCaddie.caddie_surname || ''}`.trim();
      const hasLinkedAccount = Boolean(prefilledCaddie.linked_caddie_user_id);

      setSelectedCaddie(prefilledCaddie);
      setSendRequiresOtp(false);
      setSendOtp('');
      setReferenceRequiresOtp(false);
      setReferenceOtp('');
      setShowConfirmation(false);
      setSendMoneyData({
        recipient_identifier: (
          prefilledCaddie.linked_caddie_user_id ||
          prefilledCaddie.caddie_email ||
          prefilledCaddie.caddie_phone ||
          ''
        ),
        amount: String(paymentSettings.recommended_amount),
        notes: prefilledCaddie.notes || ''
      });
      setReferenceData({
        recipient_phone: prefilledCaddie.caddie_phone || '',
        recipient_name: displayName,
        amount: String(paymentSettings.recommended_amount),
        notes: prefilledCaddie.notes || ''
      });
      setActiveTab(hasLinkedAccount ? 'registered' : 'reference');
    }
  }, [prefilledCaddie, paymentSettings.recommended_amount]);

  const renderAmountSelector = (id, data, setData) => (
    <div className="space-y-3">
      <Label>Choose an amount (ZAR)</Label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={`${id}-denominations`}>
        {paymentSettings.denominations.map((amount) => {
          const selected = Number(data.amount) === Number(amount);
          const recommended = Number(paymentSettings.recommended_amount) === Number(amount);
          return (
            <button
              key={amount}
              type="button"
              onClick={() => setData({ ...data, amount: String(amount) })}
              className={`relative min-h-12 rounded-lg border-2 px-3 py-2 font-semibold transition-colors ${
                selected
                  ? 'border-emerald-800 bg-emerald-800 text-white'
                  : 'border-slate-200 bg-white text-slate-800 hover:border-emerald-500'
              }`}
              aria-pressed={selected}
              data-testid={`${id}-denomination-${amount}`}
            >
              R{amount}
              {recommended ? (
                <span className={`block text-[10px] font-medium ${selected ? 'text-emerald-100' : 'text-emerald-700'}`}>
                  Recommended
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {paymentSettings.allow_custom_amount ? (
        <div className="space-y-2">
          <Label htmlFor={id}>Other amount</Label>
          <Input
            id={id}
            type="number"
            step="0.01"
            min="1"
            placeholder="Enter amount"
            value={data.amount}
            onChange={(event) => setData({ ...data, amount: event.target.value })}
            required
            className="input-field"
            data-testid={`${id}-input`}
          />
        </div>
      ) : (
        <p className="text-xs text-slate-500">Select one of the available amounts.</p>
      )}
    </div>
  );

  const handleSendToRegistered = async (e) => {
    e.preventDefault();
    try {
      if (!sendRequiresOtp) {
        const otpResponse = await walletAPI.sendMoney({
          recipient_identifier: sendMoneyData.recipient_identifier,
          amount: parseFloat(sendMoneyData.amount),
          notes: sendMoneyData.notes
        });

        const requiresOtp =
          otpResponse.data?.requires_otp === true ||
          otpResponse.data?.requires_otp === 'true' ||
          (typeof otpResponse.data?.message === 'string' && otpResponse.data.message.toLowerCase().includes('otp'));

        if (requiresOtp) {
          setSendRequiresOtp(true);
          toast.info('OTP sent to your phone. It is valid for 5 minutes.');
          return;
        }
      }

      const response = await walletAPI.sendMoney({
        recipient_identifier: sendMoneyData.recipient_identifier,
        amount: parseFloat(sendMoneyData.amount),
        notes: sendMoneyData.notes,
        otp_code: sendOtp.trim()
      });
      toast.success(`Sent R${sendMoneyData.amount} to ${response.data.recipient_name}`);
      setSendMoneyData({ recipient_identifier: '', amount: '', notes: '' });
      setSearchResults([]);
      setSendRequiresOtp(false);
      setSendOtp('');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const handleResendSendOtp = async () => {
    if (!sendMoneyData.recipient_identifier || !sendMoneyData.amount) {
      toast.error('Select a caddie and enter an amount first');
      return;
    }
    try {
      await walletAPI.sendMoney({
        recipient_identifier: sendMoneyData.recipient_identifier,
        amount: parseFloat(sendMoneyData.amount),
        notes: sendMoneyData.notes
      });
      setSendRequiresOtp(true);
      setSendOtp('');
      toast.info('OTP resent. Previous code has been invalidated.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    }
  };

  const handleSendViaReference = async (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const confirmAndSendReference = async () => {
    try {
      if (!referenceRequiresOtp) {
        const otpResponse = await walletAPI.sendViaReference({
          recipient_phone: referenceData.recipient_phone,
          recipient_name: referenceData.recipient_name || null,
          amount: parseFloat(referenceData.amount),
          notes: referenceData.notes
        });

        const requiresOtp =
          otpResponse.data?.requires_otp === true ||
          otpResponse.data?.requires_otp === 'true' ||
          (typeof otpResponse.data?.message === 'string' && otpResponse.data.message.toLowerCase().includes('otp'));

        if (requiresOtp) {
          setReferenceRequiresOtp(true);
          toast.info('OTP sent to your phone. It is valid for 5 minutes.');
          return;
        }
      }

      const response = await walletAPI.sendViaReference({
        recipient_phone: referenceData.recipient_phone,
        recipient_name: referenceData.recipient_name || null,
        amount: parseFloat(referenceData.amount),
        notes: referenceData.notes,
        otp_code: referenceOtp.trim()
      });
      setGeneratedReference(response.data);
      toast.success(
        response.data?.provider === 'atlas'
          ? 'Atlas voucher created and redemption code sent to the caddie.'
          : 'Collection reference generated!'
      );
      setReferenceData({ recipient_phone: '', recipient_name: '', amount: '', notes: '' });
      setShowConfirmation(false);
      setReferenceRequiresOtp(false);
      setReferenceOtp('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    }
  };

  const handleResendReferenceOtp = async () => {
    if (!referenceData.recipient_phone || !referenceData.amount) {
      toast.error('Enter recipient phone and amount first');
      return;
    }
    try {
      await walletAPI.sendViaReference({
        recipient_phone: referenceData.recipient_phone,
        recipient_name: referenceData.recipient_name || null,
        amount: parseFloat(referenceData.amount),
        notes: referenceData.notes
      });
      setReferenceRequiresOtp(true);
      setReferenceOtp('');
      toast.info('OTP resent. Previous code has been invalidated.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    }
  };

  const handleSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await searchCaddies(query);
      setSearchResults(response);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleClose = () => {
    setGeneratedReference(null);
    setSendMoneyData({ recipient_identifier: '', amount: '', notes: '' });
    setReferenceData({ recipient_phone: '', recipient_name: '', amount: '', notes: '' });
    setSearchResults([]);
    setShowConfirmation(false);
    setSelectedCaddie(null);
    setActiveTab('registered');
    setSendRequiresOtp(false);
    setSendOtp('');
    setReferenceRequiresOtp(false);
    setReferenceOtp('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-xl max-h-[calc(100dvh-1rem)] gap-0 overflow-hidden rounded-2xl p-0"
        data-testid="send-money-dialog"
      >
        <div className="flex max-h-[calc(100dvh-1rem)] flex-col">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-4 pb-4 pt-5 text-left sm:px-6">
            <DialogTitle className="pr-10 text-xl leading-tight sm:text-2xl">
              {prefilledCaddie ? (
                <span>
                  Send Money to Caddie:{' '}
                  <span className="text-emerald-900">{prefilledCaddie.caddie_name} {prefilledCaddie.caddie_surname}</span>
                </span>
              ) : (
                'Send Money to Caddie'
              )}
            </DialogTitle>
            <DialogDescription className="pr-10 text-sm leading-relaxed text-slate-600">
              Choose how to send: to a registered caddie or by SMS cash pickup
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            {!generatedReference ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-4 grid h-auto w-full grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white p-1">
                  <TabsTrigger
                    value="registered"
                    disabled={selectedCaddie && !hasLinkedCaddiePayAccount(selectedCaddie)}
                    data-testid="registered-tab"
                    className="min-h-[3rem] whitespace-normal rounded-lg px-2 py-2 text-center text-xs font-semibold leading-snug transition-all sm:px-3 sm:py-3 sm:text-sm data-[state=active]:bg-emerald-900 data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-50"
                  >
                    Registered Caddie
                  </TabsTrigger>
                  <TabsTrigger
                    value="reference"
                    data-testid="reference-tab"
                    className="min-h-[3rem] whitespace-normal rounded-lg px-2 py-2 text-center text-xs font-semibold leading-snug transition-all sm:px-3 sm:py-3 sm:text-sm data-[state=active]:bg-emerald-900 data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-50"
                  >
                    Via Cash Pickup
                  </TabsTrigger>
                </TabsList>

                {/* Send to Registered Caddie */}
                <TabsContent value="registered" className="mt-0">
                  {!selectedCaddie ? (
                    <div className="space-y-3">
                      <Label className="text-slate-700">Select a caddie</Label>
                      {registeredCaddies.length === 0 ? (
                        <p className="py-6 text-center text-sm text-slate-500">No registered caddies yet. Register one from "My Caddies".</p>
                      ) : (
                        <div className="max-h-64 space-y-2 overflow-y-auto" data-testid="caddie-select-list">
                          {registeredCaddies.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => selectCaddieForPayment(c)}
                              className="flex w-full items-center gap-3 rounded-lg border border-slate-200 p-3 text-left transition-all hover:border-emerald-400 hover:bg-emerald-50"
                              data-testid={`select-caddie-${c.id}`}
                            >
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                                <User className="h-4 w-4 text-emerald-700" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-slate-900">{c.caddie_name} {c.caddie_surname}</p>
                                <p className="truncate text-xs text-slate-500">{c.caddie_phone}</p>
                                <p className="truncate text-[11px] text-slate-400">
                                  {hasLinkedCaddiePayAccount(c) ? 'CaddiePay wallet transfer' : 'Cash pickup reference'}
                                </p>
                              </div>
                              <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-400" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSendToRegistered} className="space-y-4">
                      <div
                        className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-emerald-500 bg-emerald-50 p-3"
                        onClick={() => {
                          setSelectedCaddie(null);
                          setActiveTab('registered');
                        }}
                        data-testid="selected-caddie-card"
                      >
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-200">
                          <User className="h-4 w-4 text-emerald-800" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-emerald-900">{selectedCaddie.caddie_name} {selectedCaddie.caddie_surname}</p>
                          <p className="text-xs text-emerald-700">{selectedCaddie.caddie_phone}</p>
                        </div>
                        <span className="flex-shrink-0 text-xs text-emerald-600 hover:underline">Change</span>
                      </div>
                      {renderAmountSelector('send-amount', sendMoneyData, setSendMoneyData)}
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notes (optional)</Label>
                        <Input
                          id="notes"
                          type="text"
                          placeholder="Tip for great service"
                          value={sendMoneyData.notes}
                          onChange={(e) => setSendMoneyData({ ...sendMoneyData, notes: e.target.value })}
                          className="input-field"
                          data-testid="send-notes-input"
                        />
                      </div>
                      {sendRequiresOtp && (
                        <div className="space-y-2">
                          <Label htmlFor="send-otp">SMS OTP</Label>
                          <Input
                            id="send-otp"
                            type="text"
                            inputMode="numeric"
                            placeholder="Enter 6-digit OTP"
                            value={sendOtp}
                            onChange={(e) => setSendOtp(e.target.value)}
                            required
                            className="input-field"
                            data-testid="send-otp-input"
                          />
                          <button
                            type="button"
                            onClick={handleResendSendOtp}
                            className="text-xs text-emerald-800 underline underline-offset-2"
                            data-testid="send-resend-otp-btn"
                          >
                            Resend OTP (invalidates previous code)
                          </button>
                        </div>
                      )}
                      <Button type="submit" className="w-full btn-primary" data-testid="send-confirm-btn">
                        {sendRequiresOtp ? 'Verify OTP & Send' : 'Send OTP'}
                      </Button>
                    </form>
                  )}
                </TabsContent>

                {/* Send Via Reference Code */}
                <TabsContent value="reference" className="mt-0">
                  {selectedCaddie && !hasLinkedCaddiePayAccount(selectedCaddie) && (
                    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4">
                      <p className="text-sm font-medium text-emerald-900">
                        {resolveCaddieDisplayName(selectedCaddie)} is saved in My Caddies but does not have a CaddiePay wallet yet.
                      </p>
                      <p className="mt-1 text-xs text-emerald-700">
                        We will generate a cash pickup reference and PIN so they can collect the money without needing a smartphone.
                      </p>
                    </div>
                  )}

                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 sm:p-4">
                    <p className="text-sm font-medium text-amber-900">📱 For caddies without smartphones</p>
                    <p className="mt-1 text-xs text-amber-700">Send cash-pickup credentials the caddie can redeem without installing CaddiePay</p>
                  </div>

                  {!showConfirmation ? (
                    <form onSubmit={handleSendViaReference} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="ref-phone">Caddie Phone Number *</Label>
                        <Input
                          id="ref-phone"
                          type="tel"
                          placeholder="+27821234567"
                          value={referenceData.recipient_phone}
                          onChange={(e) => setReferenceData({ ...referenceData, recipient_phone: e.target.value })}
                          required
                          className="input-field"
                          data-testid="ref-phone-input"
                        />
                        <p className="text-xs text-slate-500">We'll send SMS notification (if available)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ref-name">Caddie Name (optional)</Label>
                        <Input
                          id="ref-name"
                          type="text"
                          placeholder="John Doe"
                          value={referenceData.recipient_name}
                          onChange={(e) => setReferenceData({ ...referenceData, recipient_name: e.target.value })}
                          className="input-field"
                          data-testid="ref-name-input"
                        />
                      </div>
                      {renderAmountSelector('ref-amount', referenceData, setReferenceData)}
                      <div className="space-y-2">
                        <Label htmlFor="ref-notes">Notes (optional)</Label>
                        <Input
                          id="ref-notes"
                          type="text"
                          placeholder="Payment for services"
                          value={referenceData.notes}
                          onChange={(e) => setReferenceData({ ...referenceData, notes: e.target.value })}
                          className="input-field"
                          data-testid="ref-notes-input"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary" data-testid="ref-generate-btn">
                        Continue
                      </Button>
                    </form>
                  ) : (
                    /* Confirmation Step */
                    <div className="space-y-4" data-testid="confirmation-dialog">
                      <div className="rounded-xl border-2 border-red-500 bg-red-50 p-4 sm:p-6">
                        <div className="flex flex-col items-start gap-4 sm:flex-row">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-xl font-bold text-white">
                            !
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="mb-2 text-base font-bold leading-snug text-red-900 sm:text-lg">⚠️ Important: Confirm Recipient Details</h3>
                            <p className="mb-3 text-sm leading-relaxed text-red-800">
                              Once confirmed, the amount is deducted from your wallet and cash-pickup credentials are issued to the caddie's phone.
                            </p>
                            <ul className="list-disc space-y-1 pl-5 text-sm text-red-800">
                              <li>The caddie does not need a CaddiePay account</li>
                              <li>Credentials are sent to the mobile number entered</li>
                              <li>Redemption instructions and expiry are confirmed after processing</li>
                              <li>Make sure the amount and phone number are correct</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <Card className="border-2 border-slate-300 bg-slate-50">
                        <CardContent className="pt-5 sm:pt-6">
                          <h3 className="mb-4 text-center font-bold text-slate-900">Please Confirm Transaction Details</h3>
                          <div className="space-y-3">
                            <div className="flex flex-col gap-1 border-b py-2 sm:flex-row sm:items-center sm:justify-between">
                              <span className="text-slate-600">Recipient Phone:</span>
                              <span className="break-words text-left font-semibold text-slate-900 sm:text-right">{referenceData.recipient_phone}</span>
                            </div>
                            {referenceData.recipient_name && (
                              <div className="flex flex-col gap-1 border-b py-2 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-slate-600">Recipient Name:</span>
                                <span className="break-words text-left font-semibold text-slate-900 sm:text-right">{referenceData.recipient_name}</span>
                              </div>
                            )}
                            <div className="flex flex-col gap-1 border-b py-2 sm:flex-row sm:items-center sm:justify-between">
                              <span className="text-slate-600">Amount:</span>
                              <span className="text-2xl font-bold text-emerald-900">R{parseFloat(referenceData.amount).toFixed(2)}</span>
                            </div>
                            {referenceData.notes && (
                              <div className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-slate-600">Notes:</span>
                                <span className="break-words text-left text-slate-900 sm:text-right">{referenceData.notes}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <p className="text-xs text-blue-900">
                          {referenceRequiresOtp
                            ? 'For security, enter the SMS OTP sent to your phone to continue. OTP expires in 5 minutes.'
                            : `By confirming, R${parseFloat(referenceData.amount).toFixed(2)} will be prepared for cash pickup and the caddie will receive the applicable credentials by SMS.`}
                        </p>
                      </div>

                      {referenceRequiresOtp && (
                        <div className="space-y-2">
                          <Label htmlFor="ref-otp">SMS OTP</Label>
                          <Input
                            id="ref-otp"
                            type="text"
                            inputMode="numeric"
                            placeholder="Enter 6-digit OTP"
                            value={referenceOtp}
                            onChange={(e) => setReferenceOtp(e.target.value)}
                            required
                            className="input-field"
                            data-testid="ref-otp-input"
                          />
                          <button
                            type="button"
                            onClick={handleResendReferenceOtp}
                            className="text-xs text-emerald-800 underline underline-offset-2"
                            data-testid="ref-resend-otp-btn"
                          >
                            Resend OTP (invalidates previous code)
                          </button>
                        </div>
                      )}

                      <div className="flex flex-col-reverse gap-3 sm:flex-row">
                        <Button
                          onClick={() => {
                            setShowConfirmation(false);
                            setReferenceRequiresOtp(false);
                            setReferenceOtp('');
                          }}
                          variant="outline"
                          className="flex-1"
                          data-testid="cancel-confirm-btn"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={confirmAndSendReference}
                          className="flex-1 btn-primary bg-red-600 hover:bg-red-700"
                          data-testid="final-confirm-btn"
                        >
                          {referenceRequiresOtp ? 'Verify OTP & Send' : 'Confirm & Send OTP'}
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
          /* Show Generated Reference */
          <div className="space-y-4" data-testid="reference-generated">
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 text-center">
              {generatedReference.provider === 'atlas' ? (
                <div data-testid="atlas-cash-pickup-generated">
                  <p className="text-sm text-green-700 font-medium mb-4">✅ Atlas Cash Pickup Created!</p>
                  <div className="bg-white rounded-lg p-4">
                    <p className="font-semibold text-emerald-900">Redemption code sent by SMS</p>
                    <p className="mt-2 text-sm text-slate-600">
                      The code was sent directly to the caddie's mobile number and is not displayed in CaddiePay.
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-green-700 font-medium mb-4">✅ Collection Reference Generated!</p>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <p className="text-xs text-slate-600 mb-2">REFERENCE CODE</p>
                    <div className="flex items-center justify-center gap-2">
                      <p className="reference-code text-2xl sm:text-3xl break-all" data-testid="generated-ref-code">{generatedReference.reference_code}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(generatedReference.reference_code, 'code')}
                        data-testid="copy-code-btn"
                      >
                        {copied.code ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-slate-600 mb-2">PIN CODE</p>
                    <div className="flex items-center justify-center gap-2">
                      <p className="font-mono text-3xl sm:text-4xl font-bold text-emerald-900" data-testid="generated-pin">{generatedReference.pin_code}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(generatedReference.pin_code, 'pin')}
                        data-testid="copy-pin-btn"
                      >
                        {copied.pin ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Card className="bg-slate-50 p-4">
              <div className="space-y-2 text-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-bold text-emerald-900">R{generatedReference.amount.toFixed(2)}</span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-slate-600">Caddie Phone:</span>
                  <span className="break-words font-semibold">{generatedReference.recipient_phone}</span>
                </div>
                {generatedReference.recipient_name && (
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-slate-600">Name:</span>
                    <span className="break-words font-semibold">{generatedReference.recipient_name}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-slate-600">Valid Until:</span>
                  <span>
                    {generatedReference.provider === 'atlas'
                      ? new Date(generatedReference.expires_at).toLocaleString()
                      : new Date(generatedReference.expires_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Card>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">📝 Instructions for Caddie:</p>
              {generatedReference.provider === 'atlas' ? (
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Open the redemption-code SMS</li>
                  <li>Visit a participating Flash merchant before the code expires</li>
                  <li>Present the SMS code to collect R{generatedReference.amount.toFixed(2)} cash</li>
                </ol>
              ) : (
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Visit any participating retail partner</li>
                  <li>Provide the reference code and PIN</li>
                  <li>Collect R{generatedReference.amount.toFixed(2)} cash</li>
                  <li>Code expires in 30 days</li>
                </ol>
              )}
            </div>

            {generatedReference.instructions?.sms_text && (
              <div className="border-t pt-4">
                <Label className="text-xs text-slate-600 mb-2 block">SMS TEXT (Copy & Send)</Label>
                <div className="bg-slate-100 rounded-lg p-3 relative">
                  <p className="text-sm font-mono text-slate-800 pr-8" data-testid="sms-text">
                    {generatedReference.instructions.sms_text}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(generatedReference.instructions.sms_text, 'sms')}
                    data-testid="copy-sms-btn"
                  >
                    {copied.sms ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <a
                  href={`sms:${generatedReference.recipient_phone}?body=${encodeURIComponent(generatedReference.instructions.sms_text)}`}
                  className="inline-flex items-center gap-2 mt-2 text-sm text-emerald-900 hover:underline"
                  data-testid="send-sms-link"
                >
                  <MessageCircle className="w-4 h-4" />
                  Send via SMS
                </a>
              </div>
            )}

            <Button onClick={handleClose} className="w-full btn-secondary" data-testid="close-ref-btn">
              Done
            </Button>
          </div>
        )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SendMoneyDialog;
